/**
 * ProtectedRoute Component
 * Wrapper component to protect routes that require authentication
 *
 * Requirements: 9.1, 9.2, 9.4
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AccessDenied from './AccessDenied';

/**
 * ProtectedRoute wrapper component
 * Redirects to login if user is not authenticated.
 * Renders AccessDenied (French 403 screen) when the user lacks requiredRole.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.requiredRole] - Role string checked against user.roles array
 */
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#17386E] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Requirement: 9.1, 9.4 - Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role guard — show French AccessDenied screen instead of inline text
  if (requiredRole && user?.roles && !user.roles.includes(requiredRole)) {
    return <AccessDenied />;
  }

  // Requirement: 9.2 - Render children if authenticated (and role matches)
  return children;
};

export default ProtectedRoute;
